import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Menu, X, Sun, Moon, Monitor, Languages, LogOut, Check, ChevronDown, ShieldCheck } from 'lucide-react';
import { useI18n, LANGUAGES } from '../i18n/I18nProvider';
import { useTheme } from '../theme/ThemeProvider';
import { useSession } from '../session/SessionProvider';
import { Avatar, Dropdown, DropdownItem, DropdownLabel, DropdownDivider, Button } from '../ui';
import ErrorBoundary from '../components/ErrorBoundary';
import TwoFactorDialog from '../features/account/TwoFactorDialog';

/**
 * Каркас кабинета: боковое меню (на мобильных — выезжающее), верхняя панель, контент.
 * nav: [{ group, items: [{ to, label, icon, end? }] }]
 */
export default function AppShell({ nav, brand, topbarExtra, banner }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useI18n();

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 shrink-0 items-center border-b border-zinc-200 px-4 dark:border-zinc-800">{brand}</div>
      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5 custom-scrollbar" aria-label={t('topbar.menu')}>
        {nav.map((section) => (
          <div key={section.group}>
            <p className="mb-1.5 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
              {section.group}
            </p>
            <ul className="space-y-0.5">
              {section.items.map(({ to, label, icon: Icon, end }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-brand-50 text-brand-700 dark:bg-brand-950/60 dark:text-brand-300'
                          : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800/70 dark:hover:text-zinc-100'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon className={clsx('h-4 w-4 shrink-0', isActive ? 'text-brand-600 dark:text-brand-400' : 'text-zinc-400')} />
                        <span className="truncate">{label}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Боковое меню: десктоп */}
      <aside className="hidden w-64 shrink-0 border-r border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 lg:block">
        {sidebar}
      </aside>

      {/* Боковое меню: мобильные */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 animate-fade-in bg-zinc-950/40" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          <aside className="relative h-full w-72 max-w-[85vw] bg-white shadow-pop dark:bg-zinc-900">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-4 rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
              aria-label={t('common.close')}
            >
              <X className="h-5 w-5" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {banner}
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-zinc-200 bg-white/80 px-4 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/80 sm:px-6">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="rounded-lg p-2 text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800 lg:hidden"
            aria-label={t('topbar.menu')}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">{topbarExtra}</div>
          <LanguageMenu />
          <ThemeMenu />
          <UserMenu />
        </header>
        <main className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}

function LanguageMenu() {
  const { lang, setLang, t } = useI18n();
  const current = LANGUAGES.find((l) => l.code === lang);
  return (
    <Dropdown
      trigger={(open, toggle) => (
        <Button variant="ghost" size="sm" onClick={toggle} aria-label={t('topbar.language')} aria-expanded={open}>
          <Languages className="h-4 w-4" />
          <span className="hidden sm:inline">{current?.short}</span>
        </Button>
      )}
    >
      <DropdownLabel>{t('topbar.language')}</DropdownLabel>
      {LANGUAGES.map((l) => (
        <DropdownItem key={l.code} onClick={() => setLang(l.code)} active={l.code === lang}>
          <span className="flex items-center justify-between">
            {l.label}
            {l.code === lang && <Check className="h-4 w-4 text-brand-600" />}
          </span>
        </DropdownItem>
      ))}
    </Dropdown>
  );
}

function ThemeMenu() {
  const { preference, setPreference, isDark } = useTheme();
  const { t } = useI18n();
  const options = [
    { value: 'light', label: t('topbar.themeLight'), icon: Sun },
    { value: 'dark', label: t('topbar.themeDark'), icon: Moon },
    { value: 'system', label: t('topbar.themeSystem'), icon: Monitor },
  ];
  return (
    <Dropdown
      trigger={(open, toggle) => (
        <Button variant="ghost" size="icon-sm" onClick={toggle} aria-label={t('topbar.theme')} aria-expanded={open}>
          {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        </Button>
      )}
    >
      <DropdownLabel>{t('topbar.theme')}</DropdownLabel>
      {options.map(({ value, label, icon }) => (
        <DropdownItem key={value} icon={icon} onClick={() => setPreference(value)} active={preference === value}>
          {label}
        </DropdownItem>
      ))}
    </Dropdown>
  );
}

function UserMenu() {
  const { admin, role, company, isOwner, signOut } = useSession();
  const { t } = useI18n();
  const [twoFactorOpen, setTwoFactorOpen] = useState(false);
  const name = admin?.username ?? '';
  return (
    <>
    <Dropdown
      trigger={(open, toggle) => (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          <Avatar name={name} size="sm" />
          <span className="hidden text-left md:block">
            <span className="block max-w-[10rem] truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">{name}</span>
            <span className="block text-[11px] text-zinc-500 dark:text-zinc-400">{t(`roles.${role}`)}</span>
          </span>
          <ChevronDown className="hidden h-3.5 w-3.5 text-zinc-400 md:block" />
        </button>
      )}
    >
      <div className="px-2.5 py-2">
        <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">{name}</p>
        <p className="truncate text-xs text-zinc-500 dark:text-zinc-400">{admin?.email}</p>
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          {t(`roles.${role}`)}{!isOwner && company ? ` · ${company.name}` : ''}
        </p>
      </div>
      <DropdownDivider />
      <DropdownItem icon={ShieldCheck} onClick={() => setTwoFactorOpen(true)}>
        <span className="flex flex-1 items-center justify-between gap-3">
          {t('twofa.title')}
          <span
            className={clsx('h-2 w-2 shrink-0 rounded-full', admin?.totp_enabled === 1 ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-600')}
            title={admin?.totp_enabled === 1 ? t('twofa.on') : t('twofa.off')}
            aria-label={admin?.totp_enabled === 1 ? t('twofa.on') : t('twofa.off')}
          />
        </span>
      </DropdownItem>
      <DropdownItem icon={LogOut} onClick={signOut} tone="danger">{t('topbar.logout')}</DropdownItem>
    </Dropdown>
    <TwoFactorDialog open={twoFactorOpen} onClose={() => setTwoFactorOpen(false)} />
    </>
  );
}
